import { useState, useCallback, useRef, useMemo } from "react";
import type { AccordionType } from "./types";

type UseAccordionOptions = {
  type: AccordionType;
  value?: string[];
  onChange?: (value: string[]) => void;
  defaultValue?: string[];
  collapsible?: boolean;
};

export function useAccordion({
  type,
  value: controlledValue,
  onChange,
  defaultValue = [],
  collapsible = true,
}: UseAccordionOptions) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState<Set<string>>(
    () => new Set(defaultValue),
  );

  const expandedItems = useMemo(
    () => (isControlled ? new Set(controlledValue) : internalValue),
    [isControlled, controlledValue, internalValue],
  );

  // Ref to avoid stale closure — always has the latest expanded set
  const expandedRef = useRef(expandedItems);
  expandedRef.current = expandedItems;

  const toggleItem = useCallback(
    (itemValue: string) => {
      const current = expandedRef.current;
      const isExpanded = current.has(itemValue);

      let next: Set<string>;

      if (type === "single") {
        if (isExpanded) {
          next = collapsible ? new Set() : current;
        } else {
          next = new Set([itemValue]);
        }
      } else {
        next = new Set(current);
        if (isExpanded) {
          next.delete(itemValue);
        } else {
          next.add(itemValue);
        }
      }

      if (!isControlled) {
        setInternalValue(next);
      }
      onChange?.(Array.from(next));
    },
    [type, collapsible, isControlled, onChange],
  );

  return { expandedItems, toggleItem };
}
